// src/utils/getBaseUrl.js
const PRODUCTION_API_FALLBACK = 'https://api.thedoar.com/api';

const CANONICAL_PRODUCT_HOSTS = new Set([
  'todoar.co',
  'www.todoar.co',
  'doarspace.com',
  'www.doarspace.com',
  'thedoar.com',
  'www.thedoar.com',
]);

const isDevHost = (host) =>
  host === 'localhost' ||
  host === '127.0.0.1' ||
  host === '::1' ||
  host === '[::1]' ||
  /^192\.168\./.test(host) ||
  /^10\./.test(host) ||
  /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);

const isCanonicalProductHost = (host) =>
  CANONICAL_PRODUCT_HOSTS.has(String(host || '').toLowerCase());

const normalizeApiBase = (value) => {
  if (!value) return value;
  const trimmed = String(value).replace(/\/+$/, '');
  try {
    const parsed = new URL(trimmed);
    if (parsed.pathname === '' || parsed.pathname === '/') {
      return `${parsed.origin}/api`;
    }
  } catch {
    return trimmed;
  }
  return trimmed;
};

const localConfiguredBase = (value, fallback = 'http://localhost:5000/api') => {
  try {
    const parsed = new URL(value);
    if (!['http:', 'https:'].includes(parsed.protocol) || !isDevHost(parsed.hostname) || parsed.username || parsed.password) return fallback;
    return normalizeApiBase(value);
  } catch {
    return fallback;
  }
};

const productionConfiguredBase = (
  hostname = window.location.hostname,
  configuredValue = process.env.REACT_APP_BASE_URL,
) => {
  // First-party production surfaces must share one API hostname. Refresh and
  // OAuth cookies are host-scoped, so allowing one surface to bake in the raw
  // Render hostname splits one account session across two backend origins.
  if (isCanonicalProductHost(hostname)) return PRODUCTION_API_FALLBACK;

  const configured = normalizeApiBase(configuredValue);
  if (!configured) return PRODUCTION_API_FALLBACK;

  try {
    new URL(configured);
    return configured;
  } catch {
    return PRODUCTION_API_FALLBACK;
  }
};

const trustedRuntimeOverride = (value, configured) => {
  try {
    const override = new URL(value), expected = new URL(configured);
    if (override.protocol !== 'https:' || expected.protocol !== 'https:' || override.origin !== expected.origin || override.username || override.password || override.search || override.hash) return null;
    return normalizeApiBase(value);
  } catch { return null; }
};

const getBaseUrl = () => {
  const hostname = window.location.hostname;
  const localPage = isDevHost(hostname);
  const localFallback = localPage && window.location.protocol === 'https:'
    ? `${window.location.origin}/api`
    : 'http://localhost:5000/api';

  // Runtime overrides are a local/staging diagnostic only. Never let a stale
  // browser override split authentication on a canonical production surface.
  if (!isCanonicalProductHost(hostname)) {
    const forced = sessionStorage.getItem('API_BASE_URL_OVERRIDE');
    if (forced) {
      if (localPage) return localConfiguredBase(forced, localFallback);
      const trusted = trustedRuntimeOverride(forced, process.env.REACT_APP_BASE_URL);
      if (trusted) return trusted;
    }
  }

  if (isDevHost(hostname) && window.location.protocol === 'https:') {
    return `${window.location.origin}/api`;
  }

  if (process.env.NODE_ENV === 'development' || isDevHost(hostname)) {
    return localConfiguredBase(process.env.REACT_APP_BASE_URL_LOCAL, localFallback);
  }

  return productionConfiguredBase(hostname);
};

export {
  PRODUCTION_API_FALLBACK,
  CANONICAL_PRODUCT_HOSTS,
  isCanonicalProductHost,
  normalizeApiBase,
  localConfiguredBase,
  productionConfiguredBase,
  trustedRuntimeOverride,
};
export default getBaseUrl;
